/**
 * Product page carousels ([data-pdp-fade]): measures how much of a card is cut off at each edge
 * of the slider and fades exactly that part out, so fully visible cards are never faded. Runs on
 * slider scroll, resize and when sections render later (recently viewed fires section:load).
 */
(() => {
  if (window.PdpCarouselFade) return;

  const MAX_FADE = 160;
  const queued = new Set();
  let frame = 0;

  function measure(slider) {
    const box = slider.getBoundingClientRect();
    const grid = slider.querySelector(':scope > * > .slider__grid, :scope > .slider__grid');
    if (!box.width || !grid) return;

    // When every card fits there is nothing to peek at: drop the peek so the cards fill the row.
    // Never oscillates: without overflow the cards fit with or without the peek.
    const carousel = slider.parentElement.parentElement;
    const overflows = slider.scrollWidth > slider.clientWidth + 1;
    if (carousel.classList.contains('pdp-fade-fits') === overflows) {
      carousel.classList.toggle('pdp-fade-fits', !overflows);
    }

    let start = 0;
    let end = 0;
    for (const slide of grid.children) {
      const rect = slide.getBoundingClientRect();
      if (!rect.width || rect.right <= box.left || rect.left >= box.right) continue;
      // Fade the visible part of a cut-off card, but never more than ~half a card, so a card that
      // is almost fully in view (mid-swipe) only gets a soft edge
      const limit = rect.width * 0.45;
      if (rect.left < box.left - 1) start = Math.max(start, Math.min(rect.right - box.left, limit));
      if (rect.right > box.right + 1) end = Math.max(end, Math.min(box.right - rect.left, limit));
    }

    slider.style.setProperty('--pdp-fade-start', `${Math.round(Math.min(start, MAX_FADE))}px`);
    slider.style.setProperty('--pdp-fade-end', `${Math.round(Math.min(end, MAX_FADE))}px`);
  }

  function flush() {
    frame = 0;
    queued.forEach(measure);
    queued.clear();
  }

  function queue(slider) {
    queued.add(slider);
    if (!frame) frame = requestAnimationFrame(flush);
  }

  function updateAll() {
    // Direct slider of the carousel only: product cards contain an image slider with the same class
    document.querySelectorAll('[data-pdp-fade] > * > .slider').forEach(queue);
  }

  document.addEventListener('scroll', (event) => {
    const target = event.target;
    const carousel = target && target.parentElement && target.parentElement.parentElement;
    if (target.classList && target.classList.contains('slider') && carousel && carousel.hasAttribute('data-pdp-fade')) {
      queue(target);
    }
  }, { capture: true, passive: true });

  // Slides change after load (dedup fill, lazy failures, carousel clones): re-measure those carousels
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      const carousel = m.target.closest && m.target.closest('[data-pdp-fade]');
      if (carousel || [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches('[data-pdp-fade]') || n.querySelector('[data-pdp-fade]')))) {
        updateAll();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('resize', updateAll);
  window.addEventListener('load', updateAll);
  document.addEventListener('section:load', updateAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAll);
  } else {
    updateAll();
  }

  window.PdpCarouselFade = { update: updateAll };
})();
